import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { OAuthService } from './oauth.service';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  /**
   * Constructor
   */
  constructor(
    private oauth: OAuthService,
    private legacyAuthService: AuthService,
  ) {}

  /**
   * Intercept
   *
   * @param req
   * @param next
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Clone the request object
    let newReq = req.clone();

    // Check if the request has the skipAuth header specially for Autodesk APIs
    if (req.headers.has('skipAuth')) {
      // Clone the request and remove the custom skipAuth header to prevent sending it
      const modifiedReq = req.clone({
        headers: req.headers.delete('skipAuth'),
      });
      return next.handle(modifiedReq);
    }

    // Check if this is an API request (either relative /api or absolute URL to our API)
    const isApiRequest =
      req.url.startsWith('/api') ||
      req.url.startsWith(environment.api_base_url) ||
      req.url.startsWith(environment.authorization_api_base_url);

    // Request
    //
    // If the access token didn't expire, add the Authorization header.
    // We won't add the Authorization header if the access token expired.
    // This will force the server to return a "401 Unauthorized" response
    // for the protected API routes which our response interceptor will
    // catch and delete the access token from the local storage while logging
    // the user out from the app.
    if (this.oauth.accessToken && isApiRequest) {
      newReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${this.oauth.accessToken}`),
      });
    } else if (isApiRequest && !this.oauth.accessToken) {
      console.warn(`API request without token: ${req.url}`);
    }

    // Response
    return next.handle(newReq).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === 401) {
            // Only sign out if not already signing out (prevents infinite loops)
            // When signOut() is called, it sets _signingOut=true, but other API calls
            // (like /me) may still be in flight and get 401, causing this to be called again
            if (!this.legacyAuthService.isSigningOut) {
              this.legacyAuthService.signOut();
            }
            return throwError(error);
          }
        }

        return throwError(error);
      }),
    );
  }
}
